import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios'
import { message } from 'antd'

// API响应类型
export interface ApiResponse<T> {
  code?: number
  data?: T
  message?: string
  error?: string
}

export interface AlertItem {
  source: string
  severity: string
  occurred_at?: string | null
  token_id?: number | null
  token_name?: string | null
  title: string
  symbol?: string | null
  side?: string | null
  signal_id?: string | null
  order_id?: string | null
  latency_ms?: number | null
  detail?: string | null
  resolved?: boolean | null
}

export interface AlertsResponse {
  summary: {
    total: number
    order_failed: number
    slow_webhook: number
    system_exception: number
    manual_order_failed: number
  }
  items: AlertItem[]
  window: { hours: number; slow_ms: number }
}

// API基础路径：优先使用环境变量，否则使用相对路径（让nginx代理转发）
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api/v1'

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    console.log('🚀 API请求拦截器:', {
      url: config.url,
      method: config.method,
      baseURL: config.baseURL,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'null'
    })
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
      console.log('🔑 添加认证头:', `Bearer ${token.substring(0, 20)}...`)
    }
    return config
  },
  (error) => {
    console.error('❌ 请求拦截器错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ API响应成功:', {
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
      dataType: typeof response.data,
      hasData: !!response.data
    })
    return response
  },
  (error: AxiosError<ApiResponse<any>>) => {
    console.error('❌ API响应错误:', {
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      responseData: error.response?.data
    })
    
    if (error.response?.status === 401) {
      console.log('🔒 401错误，清除认证信息并重定向到登录页')
      // 未授权，跳转到登录页
      localStorage.removeItem('token')
      window.location.href = '/login'
    } else if ((error.response?.data as any)?.detail) {
      message.error((error.response?.data as any)?.detail)
    } else if (error.message) {
      message.error(error.message)
    }
    return Promise.reject(error)
  }
)

// 通用请求方法
async function request<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await apiClient.request<T>(config)
    return response.data
  } catch (error) {
    throw error
  }
}

// API方法
export const api = {
  // 认证相关
  auth: {
    login: (data: { username: string; password: string }) =>
      request<{
        access_token?: string
        token?: string
        user?: any
        expires_in?: number
        mfa_required?: boolean
        mfa_setup_required?: boolean
        mfa_token?: string
        mfa_qr_uri?: string
      }>({
        method: 'POST',
        url: '/auth/login',
        data,
      }),
    verifyMfaSetup: (data: { mfa_token: string; otp_code: string }) =>
      request<{ access_token: string; token?: string; user: any; expires_in: number }>({
        method: 'POST',
        url: '/auth/mfa/setup/verify',
        data,
      }),
    verifyMfaLogin: (data: { mfa_token: string; otp_code: string }) =>
      request<{ access_token: string; token?: string; user: any; expires_in: number }>({
        method: 'POST',
        url: '/auth/mfa/login-verify',
        data,
      }),
    logout: () =>
      request<{ message: string }>({
        method: 'POST',
        url: '/auth/logout',
      }),
  },

  // 统计相关
  statistics: {
    getOverview: () =>
      request<any>({
        method: 'GET',
        url: '/statistics/overview',
      }),
    getPerformanceSummary: async (days = 30) => {
      const res = await request<{ status?: string; data?: any }>({
        method: 'GET',
        url: '/statistics/performance',
        params: { days },
      })
      return res?.data ?? res
    },
    getDailyStats: (params?: { start_date?: string; end_date?: string }) =>
      request<any[]>({
        method: 'GET',
        url: '/statistics/daily',
        params,
      }),
    getTokenRanking: (days = 30) =>
      request<any>({
        method: 'GET',
        url: '/statistics/token-ranking',
        params: { days },
      }),
    refreshStatistics: (days = 7) =>
      request<any>({
        method: 'POST',
        url: '/statistics/refresh',
        params: { days },
      }),
  },

  // 监控相关
  monitoring: {
    getSystemStatus: () =>
      request<any>({
        method: 'GET',
        url: '/monitoring/status',
      }),
    getExchangesInfo: () =>
      request<any>({
        method: 'GET',
        url: '/monitoring/exchanges',
      }),
    getBinanceOrderLatency: () =>
      request<any>({
        method: 'GET',
        url: '/monitoring/binance/order-latency',
      }),
  },

  // 风险相关
  risk: {
    getMetrics: () =>
      request<any>({
        method: 'GET',
        url: '/risk/metrics',
      }),
    getSimpleMetrics: () =>
      request<any>({
        method: 'GET',
        url: '/risk/metrics/simple',
      }),
  },

  // Token管理
  token: {
    list: () =>
      request<any[]>({
        method: 'GET',
        url: '/tokens',
      }),
    get: (id: number) =>
      request<any>({
        method: 'GET',
        url: `/tokens/${id}`,
      }),
    getAll: () =>
      request<any[]>({
        method: 'GET',
        url: '/tokens',
      }),
    create: (data: any) =>
      request<any>({
        method: 'POST',
        url: '/tokens',
        data,
      }),
    update: (id: number, data: any) =>
      request<any>({
        method: 'PUT',
        url: `/tokens/${id}`,
        data,
      }),
    delete: (id: number) =>
      request<void>({
        method: 'DELETE',
        url: `/tokens/${id}`,
      }),
    getUsageLogs: (id: number, params?: any) =>
      request<any[]>({
        method: 'GET',
        url: `/tokens/${id}/usage-logs`,
        params,
      }),
    getExchangeStatus: (id: number) =>
      request<any>({
        method: 'GET',
        url: `/tokens/${id}/exchange-status`,
      }),
    listMutexRules: () =>
      request<any[]>({
        method: 'GET',
        url: '/tokens/mutex-rules',
      }),
    createMutexRule: (data: any) =>
      request<any>({
        method: 'POST',
        url: '/tokens/mutex-rules',
        data,
      }),
    updateMutexRule: (id: number, data: any) =>
      request<any>({
        method: 'PUT',
        url: `/tokens/mutex-rules/${id}`,
        data,
      }),
    deleteMutexRule: (id: number) =>
      request<any>({
        method: 'DELETE',
        url: `/tokens/mutex-rules/${id}`,
      }),
    // 待执行（阻塞）订单
    getPendingOrders: (tokenId: number, includeClosed = false) =>
      request<any[]>({
        method: 'GET',
        url: `/tokens/${tokenId}/pending-orders`,
        params: { include_closed: includeClosed },
      }),
    updatePendingOrder: (tokenId: number, poId: number, data: any) =>
      request<any>({
        method: 'PUT',
        url: `/tokens/${tokenId}/pending-orders/${poId}`,
        data,
      }),
    releasePendingOrder: (tokenId: number, poId: number, releaseMode: string) =>
      request<any>({
        method: 'POST',
        url: `/tokens/${tokenId}/pending-orders/${poId}/release`,
        data: { release_mode: releaseMode },
      }),
    cancelPendingOrder: (tokenId: number, poId: number) =>
      request<any>({
        method: 'POST',
        url: `/tokens/${tokenId}/pending-orders/${poId}/cancel`,
      }),
    // 挂单（open orders）
    getOpenOrders: (tokenId: number, symbol?: string) =>
      request<any[]>({
        method: 'GET',
        url: `/tokens/${tokenId}/open-orders`,
        params: symbol ? { symbol } : undefined,
      }),
    cancelOpenOrder: (tokenId: number, orderId: string, symbol: string) =>
      request<any>({
        method: 'DELETE',
        url: `/tokens/${tokenId}/open-orders/${orderId}`,
        params: { symbol },
      }),
    getExecutionStats: (tokenId: number, days = 7) =>
      request<any>({
        method: 'GET',
        url: `/tokens/${tokenId}/execution-stats`,
        params: { days },
      }),
    getAccountLeverage: (tokenId: number) =>
      request<any>({ method: 'GET', url: `/tokens/${tokenId}/account-leverage` }),
  },

  news: {
    getFlash: (limit = 50) =>
      request<import('../types/api').NewsFlashResponse>({
        method: 'GET',
        url: '/news/flash',
        params: { limit },
      }),
  },

  market: {
    getFearGreed: (limit = 30) =>
      request<import('../types/api').FearGreedResponse>({
        method: 'GET',
        url: '/market/fear-greed',
        params: { limit },
      }),
    getEtfFlows: (lookbackDays = 30) =>
      request<import('../types/api').EtfFlowsResponse>({
        method: 'GET',
        url: '/market/etf-flows',
        params: { lookback_days: lookbackDays },
      }),
  },

  // 设置相关
  settings: {
    getSettings: () => {
      console.log('🔧 API服务 - 调用settings.getSettings')
      console.log('🔧 API服务 - 请求URL: /settings')
      console.log('🔧 API服务 - baseURL应该是:', apiClient.defaults.baseURL)
      return request<any>({
        method: 'GET',
        url: '/settings',
      })
    },
    updateSettings: (data: any) =>
      request<any>({
        method: 'PUT',
        url: '/settings',
        data,
      }),
    updateExchangeSettings: (data: any) =>
      request<any>({
        method: 'PUT',
        url: '/settings/exchanges',
        data,
      }),
    updateGeneralSettings: (data: any) =>
      request<any>({
        method: 'PUT',
        url: '/settings/general',
        data,
      }),
    getHeartbeatSettings: () =>
      request<{ success?: boolean; data?: any }>({
        method: 'GET',
        url: '/settings/heartbeat',
      }),
    updateHeartbeatSettings: (data: {
      enabled: boolean
      interval_seconds: number
      alert_cooldown_seconds: number
      alert_email?: string | null
      status_report_interval_hours: number
    }) =>
      request<{ success?: boolean; data?: any; message?: string }>({
        method: 'PUT',
        url: '/settings/heartbeat',
        data,
      }),
    resetHeartbeatSettings: () =>
      request<{ success?: boolean; data?: any; message?: string }>({
        method: 'DELETE',
        url: '/settings/heartbeat',
      }),
    testExchangeConnections: () =>
      request<any>({
        method: 'GET',
        url: '/settings/test-exchanges',
      }),
    getExchangeConfigs: () =>
      request<any[]>({
        method: 'GET',
        url: '/settings/exchanges',
      }),
    updateExchangeConfig: (exchangeName: string, data: any) =>
      request<any>({
        method: 'PUT',
        url: `/settings/exchanges/${exchangeName}`,
        data,
      }),
    testExchangeConnection: (exchangeName: string) =>
      request<any>({
        method: 'POST',
        url: `/settings/exchanges/${exchangeName}/test`,
      }),
    downloadMigrationPackage: async () => {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/settings/migration-package`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!response.ok) {
        let detail = `HTTP ${response.status}`
        try {
          const err = await response.json()
          detail = err?.detail || detail
        } catch {
          // ignore parse errors
        }
        throw new Error(detail)
      }
      const blob = await response.blob()
      const contentDisposition = response.headers.get('content-disposition') || ''
      const match = contentDisposition.match(/filename=\"?([^\";]+)\"?/)
      const filename = match?.[1] || `tradingbridge_migration_${Date.now()}.tar.gz`
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      return { success: true, filename }
    },
  },

  // 风控配置
  riskConfig: {
    get: () =>
      request<any>({
        method: 'GET',
        url: '/risk-config/config',
      }),
    getConfig: () =>
      request<any>({
        method: 'GET',
        url: '/risk-config/config',
      }),
    getStatus: () =>
      request<any>({
        method: 'GET',
        url: '/risk-config/status',
      }),
    update: (data: any) =>
      request<any>({
        method: 'PUT',
        url: '/risk-config/config',
        data,
      }),
    updateConfig: (data: any) =>
      request<any>({
        method: 'PUT',
        url: '/risk-config/config',
        data,
      }),
    toggle: (data: { enabled: boolean; emergency_stop?: boolean }) =>
      request<any>({
        method: 'POST',
        url: '/risk-config/toggle',
        data,
      }),
    reset: () =>
      request<any>({
        method: 'POST',
        url: '/risk-config/reset',
      }),
  },

  dynamicRisk: {
    getStatus: () =>
      request<any>({
        method: 'GET',
        url: '/dynamic-risk/status',
      }),
    getConditionTypes: () =>
      request<any[]>({
        method: 'GET',
        url: '/dynamic-risk/condition-types',
      }),
    getActionTypes: () =>
      request<any[]>({
        method: 'GET',
        url: '/dynamic-risk/action-types',
      }),
    listRules: () =>
      request<any[]>({
        method: 'GET',
        url: '/dynamic-risk/rules',
      }),
    createRule: (data: any) =>
      request<any>({
        method: 'POST',
        url: '/dynamic-risk/rules',
        data,
      }),
    updateRule: (id: number, data: any) =>
      request<any>({
        method: 'PUT',
        url: `/dynamic-risk/rules/${id}`,
        data,
      }),
    deleteRule: (id: number) =>
      request<any>({
        method: 'DELETE',
        url: `/dynamic-risk/rules/${id}`,
      }),
    evaluateNow: () =>
      request<any>({
        method: 'POST',
        url: '/dynamic-risk/evaluate-now',
      }),
  },

  // 交易历史
  tradeHistory: {
    getTrades: (params?: any) =>
      request<any>({
        method: 'GET',
        url: '/trading/history',
        params,
      }),
    getTradeHistory: (params?: any) =>
      request<any>({
        method: 'GET',
        url: '/trading/history',
        params,
      }),
    getTradeHistorySummary: async (params?: any) => {
      const res = await request<{ status?: string; data?: any }>({
        method: 'GET',
        url: '/trading/history/summary',
        params,
      })
      return res?.data ?? res
    },
    getExchangeTradeHistory: (params?: any) =>
      request<any>({
        method: 'GET',
        url: '/trading/history/exchange',
        params,
      }),
    getExchangeTradeHistorySummary: async (params?: any) => {
      const res = await request<{ status?: string; data?: any }>({
        method: 'GET',
        url: '/trading/history/exchange/summary',
        params,
      })
      return res?.data ?? res
    },
  },

  // 交易相关
  trading: {
    getOrders: (params?: any) =>
      request<any[]>({
        method: 'GET',
        url: '/orders',
        params,
      }),
    createOrder: (data: any) =>
      request<any>({
        method: 'POST',
        url: '/orders/manual',
        data,
      }),
    cancelOrder: (orderId: string) =>
      request<any>({
        method: 'DELETE',
        url: `/orders/${orderId}`,
      }),
    getPositions: (params?: { token_id?: number }) =>
      request<any[]>({
        method: 'GET',
        url: '/positions',
        params,
      }),
    closePosition: (positionId: number, data: any) =>
      request<any>({
        method: 'POST',
        url: `/positions/${positionId}/close`,
        data,
      }),
    updatePositionLimitClose: (positionId: number, data: any) =>
      request<any>({
        method: 'PUT',
        url: `/positions/${positionId}/limit-close`,
        data,
      }),
    updatePositionStopLoss: (positionId: number, data: any) =>
      request<any>({
        method: 'PUT',
        url: `/positions/${positionId}/stop-loss`,
        data,
      }),
  },

  // 主动限价/市价下单
  manualOrders: {
    create: (data: any) =>
      request<any>({
        method: 'POST',
        url: '/manual-orders',
        data,
      }),
    list: (params?: any) =>
      request<any>({
        method: 'GET',
        url: '/manual-orders',
        params,
      }),
    get: (id: number) =>
      request<any>({
        method: 'GET',
        url: `/manual-orders/${id}`,
      }),
    refresh: (id: number) =>
      request<any>({
        method: 'POST',
        url: `/manual-orders/${id}/refresh`,
      }),
  },

  // 日志相关
  logs: {
    // 获取日志列表
    getLogs: (params?: {
      start_time?: string
      end_time?: string
      level?: string
      logger_name?: string
      trace_id?: string
      token_id?: number
      signal_id?: string
      order_id?: string
      endpoint?: string
      search_text?: string
      limit?: number
      offset?: number
    }) =>
      request<any[]>({
        method: 'GET',
        url: '/logs',
        params,
      }),
    
    // 获取日志统计
    getStats: (params?: {
      start_time?: string
      end_time?: string
      level?: string
      logger_name?: string
      endpoint?: string
    }) =>
      request<any>({
        method: 'GET',
        url: '/logs/stats',
        params,
      }),
    
    // 根据追踪ID获取日志
    getByTraceId: (traceId: string) =>
      request<any[]>({
        method: 'GET',
        url: `/logs/trace/${traceId}`,
      }),
    
    // 根据信号ID获取webhook日志
    getWebhookLogs: (signalId: string) =>
      request<any[]>({
        method: 'GET',
        url: `/logs/webhook/${signalId}`,
      }),

    getTradeEvents: (params?: {
      start_time?: string
      end_time?: string
      token_id?: number
      symbol?: string
      event_type?: string
      source?: string
      status?: string
      limit?: number
      offset?: number
    }) =>
      request<any[]>({
        method: 'GET',
        url: '/logs/trades',
        params,
      }),

    getTradeSummary: (params?: {
      start_time?: string
      end_time?: string
      token_id?: number
      symbol?: string
      event_type?: string
      source?: string
      status?: string
    }) =>
      request<any>({
        method: 'GET',
        url: '/logs/trades/summary',
        params,
      }),

    getTradeEventsSummary: (params?: {
      start_time?: string
      end_time?: string
      token_id?: number
      symbol?: string
      event_type?: string
      source?: string
      status?: string
    }) =>
      request<any>({
        method: 'GET',
        url: '/logs/trades/summary',
        params,
      }),

    getExceptionEvents: (params?: {
      start_time?: string
      end_time?: string
      token_id?: number
      category?: string
      severity?: string
      resolved?: boolean
      search?: string
      limit?: number
      offset?: number
    }) =>
      request<any[]>({
        method: 'GET',
        url: '/logs/exceptions',
        params,
      }),

    resolveException: (id: number) =>
      request<any>({
        method: 'PATCH',
        url: `/logs/exceptions/${id}/resolve`,
      }),
  },

  alerts: {
    getRecent: (hours = 24, slowMs = 5000, limit = 200) =>
      request<AlertsResponse>({
        method: 'GET',
        url: '/alerts/recent',
        params: { hours, slow_ms: slowMs, limit },
      }),
  },
}

// ---- Per-token 面板 API（独立 axios 实例，带 panel_token；401 跳面板登录页）----
const API_BASE_URL_PANEL = (import.meta as any).env?.VITE_API_URL || '/api/v1'

const panelClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL_PANEL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

panelClient.interceptors.request.use((config) => {
  const pt = localStorage.getItem('panel_token')
  if (pt && config.headers) {
    config.headers.Authorization = `Bearer ${pt}`
  }
  return config
})

panelClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('panel_token')
      const tid = localStorage.getItem('panel_token_id')
      if (tid) {
        window.location.href = '/panel-login'
      }
    }
    return Promise.reject(error)
  }
)

async function panelRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await panelClient.request<T>(config)
  return response.data
}

export const panelApi = {
  login: (username: string, password: string) =>
    panelRequest<{ access_token: string; token_id: number; token_name: string; username: string; expires_in: number }>({
      method: 'POST',
      url: '/panel/login',
      data: { username, password },
    }),
  getLoginBranding: (username: string) =>
    panelRequest<{ token_name: string | null }>({
      method: 'GET',
      url: '/panel/branding',
      params: { username },
    }),
  me: () =>
    panelRequest<any>({ method: 'GET', url: '/panel/me' }),
  refreshSession: () =>
    panelRequest<{ access_token: string; token_id: number; token_name: string; username: string; expires_in: number }>({
      method: 'POST',
      url: '/panel/refresh',
    }),
  getPanelInfo: (tokenId: number) =>
    panelRequest<any>({ method: 'GET', url: `/tokens/${tokenId}/panel-info` }),
  updatePanelConfig: (tokenId: number, data: any) =>
    panelRequest<any>({ method: 'PUT', url: `/tokens/${tokenId}/panel-config`, data }),
  getPendingOrders: (tokenId: number, includeClosed = false) =>
    panelRequest<any[]>({ method: 'GET', url: `/tokens/${tokenId}/pending-orders`, params: { include_closed: includeClosed } }),
  updatePendingOrder: (tokenId: number, poId: number, data: any) =>
    panelRequest<any>({ method: 'PUT', url: `/tokens/${tokenId}/pending-orders/${poId}`, data }),
  releasePendingOrder: (tokenId: number, poId: number, releaseMode: string) =>
    panelRequest<any>({ method: 'POST', url: `/tokens/${tokenId}/pending-orders/${poId}/release`, data: { release_mode: releaseMode } }),
  cancelPendingOrder: (tokenId: number, poId: number) =>
    panelRequest<any>({ method: 'POST', url: `/tokens/${tokenId}/pending-orders/${poId}/cancel` }),
  getOpenOrders: (tokenId: number, symbol?: string) =>
    panelRequest<any[]>({ method: 'GET', url: `/tokens/${tokenId}/open-orders`, params: symbol ? { symbol } : undefined }),
  cancelOpenOrder: (tokenId: number, orderId: string, symbol: string) =>
    panelRequest<any>({ method: 'DELETE', url: `/tokens/${tokenId}/open-orders/${orderId}`, params: { symbol } }),
  getPositions: (tokenId: number) =>
    panelRequest<any[]>({ method: 'GET', url: `/tokens/${tokenId}/positions` }),
  getTradeEvents: (tokenId: number, limit = 50) =>
    panelRequest<any[]>({ method: 'GET', url: `/tokens/${tokenId}/trade-events`, params: { limit } }),
  getExecutionStats: (tokenId: number, days = 7) =>
    panelRequest<any>({ method: 'GET', url: `/tokens/${tokenId}/execution-stats`, params: { days } }),
  getAccountLeverage: (tokenId: number) =>
    panelRequest<any>({ method: 'GET', url: `/tokens/${tokenId}/account-leverage` }),
  closePosition: (tokenId: number, positionId: number, data: any) =>
    panelRequest<any>({ method: 'POST', url: `/tokens/${tokenId}/positions/${positionId}/close`, data }),
  updateLimitClose: (tokenId: number, positionId: number, data: any) =>
    panelRequest<any>({ method: 'PUT', url: `/tokens/${tokenId}/positions/${positionId}/limit-close`, data }),
  updateStopLoss: (tokenId: number, positionId: number, data: any) =>
    panelRequest<any>({ method: 'PUT', url: `/tokens/${tokenId}/positions/${positionId}/stop-loss`, data }),
  getNewsFlash: (limit = 50) =>
    panelRequest<import('../types/api').NewsFlashResponse>({
      method: 'GET',
      url: '/news/flash',
      params: { limit },
    }),
  getFearGreed: (limit = 30) =>
    panelRequest<import('../types/api').FearGreedResponse>({
      method: 'GET',
      url: '/market/fear-greed',
      params: { limit },
    }),
  getEtfFlows: (lookbackDays = 30) =>
    panelRequest<import('../types/api').EtfFlowsResponse>({
      method: 'GET',
      url: '/market/etf-flows',
      params: { lookback_days: lookbackDays },
    }),

  // Gate TradFi CFD 盈亏统计
  cfdStats: {
    getOverview: async (params?: any) => {
      const res = await request<{ status?: string; data?: any }>({
        method: 'GET',
        url: '/cfd/stats/overview',
        params,
      })
      return res?.data ?? res
    },
    getPositions: async (params?: any) => {
      const res = await request<{ status?: string; data?: any }>({
        method: 'GET',
        url: '/cfd/stats/positions',
        params,
      })
      return res?.data ?? res
    },
    getComments: async (params?: any) => {
      const res = await request<{ status?: string; data?: any }>({
        method: 'GET',
        url: '/cfd/stats/comments',
        params,
      })
      return res?.data ?? res
    },
  },
}

export default api